import { Stack } from "@mui/material";
import React, { useCallback, useMemo } from "react";

import { SkeletonIndicator } from "components/common/AnimatedWebcam/SkeletonIndicator";
import { ProjectedAccuracy } from "components/detector/InfoChart/ProjectedMLAcc/ProjectedAccuracy";
import { DROPPED_LABEL, NOT_YET_REVIEWED_LABEL, OTHER_LABEL } from "config/LabelMapping";
import { AnswerType, useTopLevelStats, type MetricClassCounts } from "hooks/DetectorDetail/useTopLevelStats";
import { DetectorModes } from "types/createDetector";
import { ANSWER_VALUES } from "types/imageQueries";
import { FilterOptionsLabel, getFilterOptions, redirectToDataPage } from "utils/redirectDataPage";

import { DetectorMetricsTable, DetectorMetricsTableRowData } from "../DetectorMetricsTable/DetectorMetricsTable";
import { DetectorTableCell } from "../DetectorMetricsTable/types";

import { type TopLevelStatsProps } from "./props";
import { StatsSelectors } from "./StatsSelectors";
import { getLowestGroundTruthClassCount, getTotalValue } from "./utils";

const COLUMNS = ["UNCLEAR", "TOTAL"];
const UNCLEAR_COLUMNS = [
  ANSWER_VALUES.UNCLEAR,
  // eslint-disable-next-line no-underscore-dangle
  ANSWER_VALUES.__UNSURE,
  ANSWER_VALUES.NEEDS_REVIEW,
  DROPPED_LABEL,
  NOT_YET_REVIEWED_LABEL,
];

export const MultichoiceTopLevelStats = React.memo((props: TopLevelStatsProps) => {
  const { detectorData, confidenceThreshold, count } = props;
  const { answerType, handleActivityPeriodChange, handleAnswerTypeChange, loadingMetrics, metrics, period, status } =
    useTopLevelStats({ detectorData, confidenceThreshold, count });

  const getRedirect = useCallback(
    (label: FilterOptionsLabel, key?: string) =>
      redirectToDataPage({
        detectorId: detectorData.id,
        filterOptions: getFilterOptions({ label, answerType, value: key, period, mode: DetectorModes.MULTICHOICE }),
      }),
    [answerType, detectorData.id, period]
  );

  const columnHeaders = useMemo(
    () => [...(detectorData?.mode_configuration?.class_names || []), ...COLUMNS],
    [detectorData.mode_configuration?.class_names]
  );

  const getRowColumns = useCallback(
    (metric: MetricClassCounts, label: FilterOptionsLabel) => {
      if (!metric) return [];

      const columns: DetectorTableCell[] =
        detectorData.mode_configuration?.class_names?.map((choice) => ({
          key: choice,
          link: getRedirect(label, choice),
          value: metric[choice] ?? 0,
          className: choice === OTHER_LABEL ? "text-primary-dark" : "text-primary-light",
        })) || [];

      const unclearValue = UNCLEAR_COLUMNS.reduce((sum, k) => sum + (metric[k] ?? 0), 0);
      columns.push({
        key: ANSWER_VALUES.UNCLEAR,
        link: getRedirect(label, ANSWER_VALUES.UNCLEAR),
        value: unclearValue,
      });

      const totalValue = getTotalValue(metric);

      columns.push({
        key: "TOTAL",
        link: getRedirect(label, label === "Total Answered" ? "true" : undefined),
        value: totalValue,
      });
      return columns;
    },
    [detectorData.mode_configuration?.class_names, getRedirect]
  );

  const generateValuesRow = useCallback(
    (label: FilterOptionsLabel, metric: MetricClassCounts) => ({
      label,
      cells: [...getRowColumns(metric, label)],
    }),
    [getRowColumns]
  );

  const rowsData: DetectorMetricsTableRowData[] = useMemo(() => {
    if (!metrics) return [];

    return [
      generateValuesRow("Answered By ML", metrics.summary.class_counts.source_ml),
      generateValuesRow("Answered By Cloud Labelers", metrics.summary.class_counts.cloud),
      generateValuesRow("Ground Truth", metrics.summary.class_counts.cust_labeler),
      {
        ...generateValuesRow("Total Answered", metrics.summary.class_counts.total),
        className: "border-b-2 border-gray-500 border-solid",
        textClass: "font-bold",
      },
      {
        label: answerType === AnswerType.FIRST_ANSWER ? "No First Answer" : "Not Answered",
        className: "border-b-2 border-gray-200 border-solid",
        textClass: "text-neutral-400",
        cell: {
          key: "Not Answered",
          value: metrics.summary.unconfident_counts?.total || 0,
          link: getRedirect("Not Answered", "false"),
        },
        colSpan: columnHeaders.includes(OTHER_LABEL) ? columnHeaders.length + 1 : columnHeaders.length + 2,
      },
      {
        label: "Total queries",
        cell: {
          key: "Total queries",
          value: metrics.summary.total_iqs || 0,
          link: redirectToDataPage({
            detectorId: detectorData.id,
            filterOptions: getFilterOptions({ period }),
          }),
        },
        className: "border-b-2 border-gray-500 border-solid",
        textClass: "font-bold",
        colSpan: columnHeaders.includes(OTHER_LABEL) ? columnHeaders.length + 1 : columnHeaders.length + 2,
      },
    ];
  }, [metrics, generateValuesRow, answerType, getRedirect, columnHeaders, detectorData.id, period]);

  if (status) return status;

  return (
    <Stack className="w-full gap-4">
      {!detectorData ? (
        SkeletonIndicator({ loading: true, height: 58 })
      ) : (
        <ProjectedAccuracy
          totalImageQueries={detectorData.total_iqs}
          detectorData={detectorData}
          lowestGroundTruthClassCount={getLowestGroundTruthClassCount(metrics)}
        />
      )}
      <StatsSelectors
        period={period}
        handleActivityPeriodChange={handleActivityPeriodChange}
        answerType={answerType}
        handleAnswerTypeChange={handleAnswerTypeChange}
      />
      {loadingMetrics ? (
        SkeletonIndicator({ loading: true, height: 290, className: "mt-4 mb-5" })
      ) : (
        <DetectorMetricsTable columns={columnHeaders} rowsData={rowsData} maxCount={30} />
      )}
    </Stack>
  );
});
